import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Grid, Card, CardContent, CardActionArea, IconButton, useTheme, useMediaQuery } from '@mui/material';
import OnboardingButtonDock from '../../components/OnboardingButtonDock';

import './OnboardingIntegration.css';
import { setIntegrations, setCurrentStep } from '../../redux/onboardingReducer';
import PhoneIcon from '@mui/icons-material/Phone';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import CloudIcon from '@mui/icons-material/Cloud';
import LanguageIcon from '@mui/icons-material/Language';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { toast } from 'sonner';

const integrationOptions = [
  { id: 'sip', name: 'SIP Contact No.', icon: <PhoneIcon /> },
  { id: 'pstn', name: 'PSTN Numbers', icon: <PhoneAndroidIcon /> },
  { id: 'virtual', name: 'Virtual No.', icon: <CloudIcon /> },
  { id: 'cloud', name: 'Cloud Calling Application', icon: <CloudIcon /> },
  { id: 'website', name: 'Websites', icon: <LanguageIcon /> },
  { id: 'mobile', name: 'Mobile Applications', icon: <SmartphoneIcon /> },
];

const OnboardingIntegration = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedIntegrations, setSelectedIntegrations] = useState([]);
  const savedIntegrations = useSelector(state => state.onboarding.integrations);
  const userIdFromRedux = useSelector(state => state.onboarding.userId);
  const userId = userIdFromRedux || localStorage.getItem('userId');

  useEffect(() => {
    const fetchIntegrations = async () => {
      if (userId) {
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/integration?userId=${userId}`);
          if (response.data && response.data.selectedPlatforms) {
            setSelectedIntegrations(response.data.selectedPlatforms);
            dispatch(setIntegrations(response.data.selectedPlatforms));
          }
        } catch (error) {
          console.error('Failed to fetch existing integrations:', error);
        }
      }
    };

    fetchIntegrations();
  }, [userId, dispatch]);

  useEffect(() => {
    // Load saved integrations when component mounts
    if (savedIntegrations.length > 0) {
      setSelectedIntegrations(savedIntegrations);
    }
  }, [savedIntegrations]);

  const handleIntegrationToggle = (integrationId) => {
    setSelectedIntegrations((prev) => {
      const newSelection = prev.includes(integrationId)
        ? prev.filter((id) => id !== integrationId)
        : [...prev, integrationId];
      
      dispatch(setIntegrations(newSelection));
      return newSelection;
    });
  };

  const handleSave = async () => {
    if (selectedIntegrations.length === 0) {
      toast.info('Please select at least one integration option to save.');
      return;
    }

    if (!userId) {
      console.error('User ID is missing');
      return false;
    }

    try {
      const requestData = {
        userId: userId,
        selectedPlatforms: selectedIntegrations
      };
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/integration`, requestData);
      
      if (response.status === 200) {
        await dispatch(setIntegrations(selectedIntegrations));
        toast.success('Integration saved successfully');
      } else {
        throw new Error('Failed to save integrations');
      }
    } catch (error) {
      console.error('Error saving integrations:', error);
      if (error.response && error.response.data && error.response.data.error) {
        toast.error(`Error: ${error.response.data.error}`);
      } else {
        toast.error('Failed to save integrations. Please try again.');
      }
    }
  };

  return (
    <Box className="onboarding-integration-container fade-in">
      <Typography variant={isMobile ? "h5" : "h4"} gutterBottom align="center" className="bounce-in">
        AI Talker Integration Options
      </Typography>
      <Typography variant="body1" paragraph align="center" sx={{ mb: isMobile ? 2 : 3 }} className="slide-in-left">
        Select the platforms where you'd like to integrate AI Talker:
      </Typography>
      <Grid container spacing={isMobile ? 2 : 3}>
        {integrationOptions.map((option, index) => (
          <Grid item xs={6} sm={6} md={4} key={option.id}>
            <Card 
              raised={selectedIntegrations.includes(option.id)}
              className={`integration-card ${selectedIntegrations.includes(option.id) ? 'selected' : ''} fade-in`}
              style={{animationDelay: `${index * 0.1}s`}}
            >
              <CardActionArea 
                onClick={() => handleIntegrationToggle(option.id)}
                className="integration-card-action hover-effect"
              >
                <Box className="integration-icon" sx={{ color: selectedIntegrations.includes(option.id) ? 'primary.main' : 'text.secondary' }}>
                  {React.cloneElement(option.icon, { fontSize: isMobile ? "medium" : "large" })}
                </Box>
                <CardContent>
                  <Typography variant={isMobile ? "body2" : "body1"} component="div" align="center">
                    {option.name}
                  </Typography>
                </CardContent>
              </CardActionArea>
              {selectedIntegrations.includes(option.id) && (
                <IconButton 
                  className="integration-check-icon"
                  onClick={() => handleIntegrationToggle(option.id)}
                  size={isMobile ? "small" : "medium"}
                >
                  <CheckCircleIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
      <OnboardingButtonDock
        onSave={handleSave}
        onBack={() => {
          dispatch(setCurrentStep(3)); // Go back to Playground
        }}
        onNext={async () => {
          await handleSave();
          dispatch(setCurrentStep(5)); // Move to Pricing
        }}
        title="save integration"
        loading={false}
        desktopClassName="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50"
        mobileClassName="fixed bottom-8 right-8 z-50"
      />
    </Box>
  );
};

export default OnboardingIntegration;
